import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { Modal, Button, Message, Progress, Icon, Dropdown } from 'semantic-ui-react';
import { useSelector } from 'react-redux';

import Config from '../../../constants/Config';
import selectors from '../../../selectors';
import { Markdown } from '../../../lib/custom-ui';
import SignaturePad from '../../SignaturePad';
import CameraCapture from '../../CameraCapture';
import styles from './FillInspectionModal.module.scss';

// Async search select for Wheelbase Vehicle
function AsyncVehicleSelect({ field, accessToken, register, setValue, errors, styles }) {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [value, setSelectedValue] = useState('');

  const searchVehicles = async query => {
    setIsLoading(true);
    try {
      const resp = await fetch(
        `${
          Config.SERVER_BASE_URL
        }/api/integrations/wheelbase/vehicles/search?q=${encodeURIComponent(query || '')}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        setOptions(
          (data.items || []).map(v => ({
            key: v.id,
            value: String(v.id),
            text: v.vin ? `${v.label} (${v.vin})` : v.label,
          }))
        );
      }
    } catch (e) {
      // ignore
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.field}>
      <label>
        {field.label}
        {field.required && <span className={styles.required}>*</span>}
      </label>
      <Dropdown
        selection
        search
        placeholder={field.placeholder || 'Search vehicle'}
        options={options}
        loading={isLoading}
        value={value}
        onSearchChange={(_, { searchQuery }) => {
          searchVehicles(searchQuery);
        }}
        onChange={(_, { value: v }) => {
          setSelectedValue(v);
          setValue(field.name, v, { shouldValidate: true, shouldDirty: true });
        }}
        noResultsMessage="No vehicles found"
        onFocus={() => searchVehicles('')}
      />
      <input type="hidden" {...register(field.name, { required: field.required })} value={value} />
      {errors[field.name] && <span className={styles.error}>This field is required</span>}
    </div>
  );
}

// Async search select for Wheelbase Booking
function AsyncBookingSelect({ field, accessToken, register, setValue, errors, styles }) {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [value, setSelectedValue] = useState('');

  const searchBookings = async query => {
    setIsLoading(true);
    try {
      const resp = await fetch(
        `${
          Config.SERVER_BASE_URL
        }/api/integrations/wheelbase/bookings/search?q=${encodeURIComponent(query || '')}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        setOptions(
          (data.items || []).map(b => ({
            key: b.id,
            value: String(b.id),
            text: b.label || `Booking ${b.id}`,
          }))
        );
      }
    } catch (e) {
      // ignore
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.field}>
      <label>
        {field.label}
        {field.required && <span className={styles.required}>*</span>}
      </label>
      <Dropdown
        selection
        search
        placeholder={field.placeholder || 'Search booking'}
        options={options}
        loading={isLoading}
        value={value}
        onSearchChange={(_, { searchQuery }) => {
          searchBookings(searchQuery);
        }}
        onChange={(_, { value: v }) => {
          setSelectedValue(v);
          setValue(field.name, v, { shouldValidate: true, shouldDirty: true });
        }}
        noResultsMessage="No bookings found"
        onFocus={() => searchBookings('')}
      />
      <input type="hidden" {...register(field.name, { required: field.required })} value={value} />
      {errors[field.name] && <span className={styles.error}>This field is required</span>}
    </div>
  );
}

const isMobileDevice = () =>
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 767;

const FillInspectionModal = React.memo(({ assignment, cardId, onClose, onSuccess }) => {
  const accessToken = useSelector(selectors.selectAccessToken);
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [photoPreviews, setPhotoPreviews] = useState({});
  const [signatures, setSignatures] = useState({});
  const [cardContext, setCardContext] = useState(null);
  const [validationMessage, setValidationMessage] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState({});
  const contentRef = React.useRef(null);
  const isMobile = isMobileDevice();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm();

  const form = assignment.formId;
  const formSchema = form.formSchema;
  const sections = formSchema.sections || [];
  const totalSections = sections.length;
  const progress = ((currentSection + 1) / totalSections) * 100;

  // Watch all fields for conditional logic
  const watchedValues = watch();

  const currentSectionData = sections[currentSection];

  // Load defaults from backend (card extraction)
  React.useEffect(() => {
    let mounted = true;
    const loadDefaults = async () => {
      try {
        const resp = await fetch(
          `${Config.SERVER_BASE_URL}/api/cards/${cardId}/forms/${form.id}/defaults`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          }
        );
        if (resp.ok) {
          const defaults = await resp.json();
          if (mounted && defaults && Object.keys(defaults).length > 0) {
            Object.keys(defaults).forEach(key => {
              // Only set if not already modified
              if (!watchedValues[key]) {
                setValue(key, defaults[key], { shouldDirty: false });
              }
            });
          }
        }
      } catch (e) {
        // ignore
      }
    };
    if (accessToken && cardId && form.id) {
      loadDefaults();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, cardId, form.id, setValue]);

  // Restore draft if one exists (protects against data loss)
  React.useEffect(() => {
    let mounted = true;
    const loadDraft = async () => {
      try {
        const resp = await fetch(
          `${Config.SERVER_BASE_URL}/api/cards/${cardId}/forms/${form.id}/draft`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          }
        );
        if (resp.ok) {
          const data = await resp.json();
          if (mounted && data.item) {
            const draft = data.item;

            if (draft.draftData && typeof draft.draftData === 'object') {
              Object.entries(draft.draftData).forEach(([key, val]) => {
                setValue(key, val, { shouldDirty: false });
              });
            }

            if (draft.uploadedPhotos && typeof draft.uploadedPhotos === 'object') {
              setUploadedPhotoUrls(draft.uploadedPhotos);
              const restoredPreviews = {};
              Object.entries(draft.uploadedPhotos).forEach(([fieldName, urls]) => {
                if (Array.isArray(urls) && urls.length > 0) {
                  restoredPreviews[fieldName] = urls;
                }
              });
              if (Object.keys(restoredPreviews).length > 0) {
                setPhotoPreviews(restoredPreviews);
              }
            }

            if (draft.signatureData && typeof draft.signatureData === 'object') {
              setSignatures(draft.signatureData);
            }

            if (typeof draft.currentSection === 'number' && draft.currentSection > 0) {
              setCurrentSection(draft.currentSection);
            }

            setDraftRestored(true);
            console.log('📋 Draft restored — resuming from section', draft.currentSection + 1);
          }
        }
      } catch (e) {
        // Draft load is non-critical, continue without it
      }
    };
    if (accessToken && cardId && form.id) {
      loadDraft();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, cardId, form.id, setValue]);

  // Load card context (for variable fields)
  React.useEffect(() => {
    let mounted = true;
    const loadCard = async () => {
      try {
        const resp = await fetch(`${Config.SERVER_BASE_URL}/api/cards/${cardId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        });
        if (resp.ok) {
          const data = await resp.json();
          if (mounted) {
            setCardContext(data.item || data);
          }
        }
      } catch (e) {
        // ignore
      }
    };
    if (accessToken && cardId) {
      loadCard();
    }
    return () => {
      mounted = false;
    };
  }, [accessToken, cardId]);

  // Resolve variable value from card context
  const resolveVariable = key => {
    if (!cardContext || !key) return '';
    if (!key.startsWith('card.')) return '';
    const path = key.split('.').slice(1); // remove 'card'
    let cur = cardContext;
    for (const segment of path) {
      if (cur && Object.prototype.hasOwnProperty.call(cur, segment)) {
        cur = cur[segment];
      } else {
        return '';
      }
    }
    if (cur == null) return '';
    return String(cur);
  };

  // Prefill variable fields once card context is available
  React.useEffect(() => {
    if (!cardContext) return;
    try {
      sections.forEach(sec => {
        (sec.fields || []).forEach(f => {
          if (f.type === 'variable') {
            const val = resolveVariable(f.variableKey || 'card.name');
            if (val !== undefined) {
              // eslint-disable-next-line no-undef
              setValue && setValue(f.name, val, { shouldDirty: false });
            }
          }
        });
      });
    } catch (_) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardContext]);

  // Scroll to top when section changes
  React.useEffect(() => {
    if (contentRef.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [currentSection]);

  const handleFileChange = (fieldName, files) => {
    if (!files || files.length === 0) {
      return;
    }

    // ACUMULAR previews (no reemplazar)
    const fileArray = Array.from(files);
    const newPreviews = [];
    let loadedCount = 0;

    fileArray.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = e => {
        newPreviews[index] = e.target.result;
        loadedCount++;

        // Cuando todas las NUEVAS imágenes estén cargadas
        if (loadedCount === fileArray.length) {
          setPhotoPreviews(prev => {
            // Obtener previews anteriores de este campo
            const existingPreviews = prev[fieldName] || [];

            // AGREGAR las nuevas al final
            return {
              ...prev,
              [fieldName]: [...existingPreviews, ...newPreviews.filter(p => p)],
            };
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (fieldName, index) => {
    setPhotoPreviews(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index),
    }));
  };

  const handleNext = async () => {
    setValidationMessage(null);

    // Get required field names from current section (excluding static fields)
    const currentFields = currentSectionData.fields || [];
    const requiredFieldNames = currentFields
      .filter(f => f.required && f.type !== 'static_text' && f.type !== 'link')
      .map(f => f.name);

    // Validate only current section fields
    const isValid = await trigger(requiredFieldNames);

    // Additional validation for camera/file fields (not handled by react-hook-form)
    const requiredCameraFields = currentFields.filter(
      f => f.required && (f.type === 'camera' || f.type === 'file')
    );
    const missingPhotos = requiredCameraFields.filter(
      f => !photoPreviews[f.name] || photoPreviews[f.name].length === 0
    );

    if (!isValid || missingPhotos.length > 0) {
      // Get names of invalid fields for user message
      const invalidFields = requiredFieldNames.filter(name => errors[name]);
      const fieldLabels = currentFields
        .filter(f => invalidFields.includes(f.name))
        .map(f => f.label);

      // Add missing photo fields to the message
      const missingPhotoLabels = missingPhotos.map(f => f.label);
      const allMissingLabels = [...fieldLabels, ...missingPhotoLabels];

      setValidationMessage(
        `Please complete the following required fields to continue: ${allMissingLabels.join(', ')}`
      );
      return;
    }

    if (currentSection < totalSections - 1) {
      const nextSection = currentSection + 1;
      setCurrentSection(nextSection);

      // Save draft in background (non-blocking for UX)
      saveDraft(nextSection);
    }
  };

  const saveDraft = async sectionToSave => {
    setIsSavingDraft(true);
    try {
      // Upload any new base64 photos from the current section to Firebase
      const currentFields = sections[sectionToSave - 1]?.fields || [];
      const photoFields = currentFields.filter(f => f.type === 'camera' || f.type === 'file');

      const newUploaded = { ...uploadedPhotoUrls };

      for (const field of photoFields) {
        const previews = photoPreviews[field.name];
        if (!Array.isArray(previews) || previews.length === 0) continue;

        const urls = [];
        for (const photo of previews) {
          const isBase64 =
            typeof photo === 'string' &&
            (photo.startsWith('data:image/') || photo.includes(';base64,'));

          if (isBase64) {
            try {
              const uploadResp = await fetch(
                `${Config.SERVER_BASE_URL}/api/firebase/upload-image`,
                {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                  },
                  credentials: 'include',
                  body: JSON.stringify({ imageData: photo, formId: form.id }),
                }
              );
              if (uploadResp.ok) {
                const result = await uploadResp.json();
                urls.push(result.url);
              } else {
                urls.push(photo);
              }
            } catch (e) {
              urls.push(photo);
            }
          } else {
            urls.push(photo);
          }
        }

        newUploaded[field.name] = urls;

        // Replace base64 previews with uploaded URLs
        setPhotoPreviews(prev => ({ ...prev, [field.name]: urls }));
      }

      setUploadedPhotoUrls(newUploaded);

      // Collect all form values accumulated so far
      const allValues = watch();

      await fetch(`${Config.SERVER_BASE_URL}/api/cards/${cardId}/forms/${form.id}/draft`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          cardId,
          formId: form.id,
          draftData: allValues,
          uploadedPhotos: newUploaded,
          signatureData: signatures,
          currentSection: sectionToSave,
        }),
      });
      console.log('💾 Draft saved — section', sectionToSave);
    } catch (e) {
      console.warn('Draft save failed (non-critical):', e);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const deleteDraft = async () => {
    try {
      await fetch(`${Config.SERVER_BASE_URL}/api/cards/${cardId}/forms/${form.id}/draft`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
      });
    } catch (e) {
      // non-critical
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  // Upload photos to Firebase Storage
  const uploadPhotosToFirebase = async photoData => {
    const uploadedPhotos = {};

    for (const [fieldName, photos] of Object.entries(photoData)) {
      if (!Array.isArray(photos) || photos.length === 0) {
        continue;
      }

      const uploadedUrls = [];

      for (const photo of photos) {
        // Check robusto para base64
        const isBase64 =
          typeof photo === 'string' &&
          (photo.startsWith('data:image/') || photo.includes(';base64,'));

        if (isBase64) {
          try {
            const uploadResponse = await fetch(
              `${Config.SERVER_BASE_URL}/api/firebase/upload-image`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                  imageData: photo,
                  formId: form.id,
                }),
              }
            );

            if (uploadResponse.ok) {
              const result = await uploadResponse.json();
              uploadedUrls.push(result.url);
              console.log('✅ Image uploaded:', result.url);
            } else {
              const errorText = await uploadResponse.text();
              console.error('Failed to upload image. Status:', uploadResponse.status, errorText);
              // Si falla el upload, NO enviamos el base64 para no romper la BD/Servidor
              // alert(`Error uploading image. Please try smaller files.`);
            }
          } catch (error) {
            console.error('Error uploading image:', error);
          }
        } else {
          // No es base64, mantenerlo como está (asumir URL)
          uploadedUrls.push(photo);
        }
      }

      uploadedPhotos[fieldName] = uploadedUrls;
    }

    return uploadedPhotos;
  };

  const onSubmit = async data => {
    if (currentSection !== totalSections - 1) {
      return;
    }

    // Validate camera/file fields before submitting
    const currentFields = currentSectionData.fields || [];
    const requiredCameraFields = currentFields.filter(
      f => f.required && (f.type === 'camera' || f.type === 'file')
    );
    const missingPhotos = requiredCameraFields.filter(
      f => !photoPreviews[f.name] || photoPreviews[f.name].length === 0
    );

    if (missingPhotos.length > 0) {
      const missingPhotoLabels = missingPhotos.map(f => f.label);
      setValidationMessage(
        `Please complete the following required fields before submitting: ${missingPhotoLabels.join(
          ', '
        )}`
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Subir fotos a Firebase Storage primero
      console.log('📤 Uploading photos to Firebase Storage...');
      const uploadedPhotos = await uploadPhotosToFirebase(photoPreviews);

      // 2. Combinar datos del form con firmas Y URLs de fotos
      const completeData = {
        ...data,
        ...signatures, // Agregar firmas al response data
        ...uploadedPhotos, // Agregar URLs de fotos (no base64)
      };

      const payload = {
        formId: form.id,
        cardId: cardId,
        inspectionType: 'general',
        responseData: completeData,
      };

      // Solo agregar signatureData si existe
      const signature = signatures.inspectorSignature || signatures.customerSignature;
      if (signature) {
        payload.signatureData = signature;
      }

      console.log('📝 Submitting inspection response:', payload);

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/inspection-responses`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        deleteDraft();
        setShowSuccessMessage(true);
        onSuccess();
        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        const error = await response.json();
        setSubmitError(error.message || 'Failed to submit inspection');
      }
    } catch (error) {
      setSubmitError('Error submitting inspection');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = field => {
    // Check conditional logic
    if (field.conditional) {
      const conditionField = field.conditional.field;
      const conditionValue = field.conditional.value;
      const conditionOperator = field.conditional.operator;
      const currentValue = watchedValues[conditionField];

      let showField = false;
      switch (conditionOperator) {
        case '===':
          showField = currentValue === conditionValue;
          break;
        case '!==':
          showField = currentValue !== conditionValue;
          break;
        default:
          showField = true;
      }

      if (!showField) {
        return null;
      }
    }

    const fieldKey = field.id || field.name;

    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input
              type={field.type}
              {...register(field.name, { required: field.required })}
              placeholder={field.placeholder || field.label}
            />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'card_extraction': {
        const extractedValue = watchedValues[field.name] || '';
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            {/* Hidden input for form submission */}
            <input type="hidden" {...register(field.name, { required: field.required })} />
            {/* Markdown formatted preview */}
            <div
              className={styles.cardExtractionPreview}
              style={{
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #dee2e6',
                minHeight: '60px',
                maxHeight: '300px',
                overflowY: 'auto',
              }}
            >
              {extractedValue ? (
                <Markdown linkStopPropagation linkTarget="_blank">
                  {extractedValue}
                </Markdown>
              ) : (
                <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                  {field.placeholder || 'No data extracted'}
                </span>
              )}
            </div>
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );
      }

      case 'textarea':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <textarea
              {...register(field.name, { required: field.required })}
              rows={field.rows || 3}
              placeholder={field.label}
            />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'select':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <select {...register(field.name, { required: field.required })}>
              <option value="">-- Select --</option>
              {field.options?.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'radio':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.radioGroup}>
              {field.options?.map(option => (
                <label key={option} className={styles.radioOption}>
                  <input
                    type="radio"
                    value={option}
                    {...register(field.name, { required: field.required })}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.checkboxGroup}>
              {field.options?.map(option => (
                <label key={option} className={styles.checkboxOption}>
                  <input type="checkbox" value={option} {...register(field.name)} />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'date':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input type="date" {...register(field.name, { required: field.required })} />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'time':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input type="time" {...register(field.name, { required: field.required })} />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'camera':
      case 'file': {
        const hasPhotos = photoPreviews[field.name]?.length > 0;
        const showError =
          field.required && !hasPhotos && (validationMessage || Object.keys(errors).length > 0);
        const isCamera = field.type === 'camera';

        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>

            {isCamera ? (
              isMobile ? (
                <label className={styles.nativeCameraBtn}>
                  <Icon name="camera" />
                  <span>{hasPhotos ? 'Take Another Photo' : 'Take Photo'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={e => {
                      handleFileChange(field.name, e.target.files);
                      if (validationMessage) setValidationMessage(null);
                    }}
                  />
                </label>
              ) : (
                <CameraCapture
                  onCapture={photos => {
                    setPhotoPreviews(prev => {
                      const existingPreviews = prev[field.name] || [];
                      return {
                        ...prev,
                        [field.name]: [...existingPreviews, ...photos],
                      };
                    });
                    if (validationMessage) setValidationMessage(null);
                  }}
                  multiple={field.multiple}
                  fieldLabel={field.label}
                />
              )
            ) : (
              <div className={styles.fileUpload}>
                <Icon name="file" size="large" />
                <input
                  type="file"
                  accept={field.accept || 'image/*'}
                  multiple
                  onChange={e => {
                    handleFileChange(field.name, e.target.files);
                    if (validationMessage) setValidationMessage(null);
                  }}
                />
                <p>{field.photoStage || 'Click to upload'}</p>
              </div>
            )}

            {hasPhotos && (
              <div className={styles.photoPreviewContainer}>
                {photoPreviews[field.name].map((preview, idx) => (
                  <div key={idx} className={styles.photoPreview}>
                    <img src={preview} alt={`Preview ${idx + 1}`} />
                    <Button
                      circular
                      icon="close"
                      size="mini"
                      color="red"
                      className={styles.removeBtn}
                      onClick={() => removePhoto(field.name, idx)}
                    />
                  </div>
                ))}
              </div>
            )}

            {showError && <span className={styles.error}>At least one photo is required</span>}
          </div>
        );
      }

      case 'signature':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <SignaturePad
              onChange={dataUrl => {
                setSignatures(prev => ({
                  ...prev,
                  [field.name]: dataUrl,
                }));
              }}
              required={field.required}
            />
            {field.required && !signatures[field.name] && errors.submit && (
              <span className={styles.error}>Signature is required</span>
            )}
          </div>
        );

      case 'static_text':
        return (
          <div
            key={fieldKey}
            className={styles.staticText}
            dangerouslySetInnerHTML={{ __html: field.formattedText || field.label }}
          />
        );

      case 'link': {
        const url = field.url || field.label;
        const idMatch = url && url.match(/\/d\/([-\w]+)/);
        const fileId = idMatch ? idMatch[1] : null;
        const isGoogleDrive = url && url.includes('drive.google.com');

        return (
          <div key={fieldKey} className={styles.field}>
            <div className={styles.linkContainer}>
              <Icon name="linkify" />
              <a href={url} target="_blank" rel="noopener noreferrer">
                {field.label || field.url}
              </a>
            </div>
            {isGoogleDrive && fileId && (
              <div className={styles.linkImagePreview}>
                <img
                  src={`https://drive.google.com/thumbnail?id=${fileId}&sz=w640`}
                  alt="Preview"
                  referrerPolicy="no-referrer"
                  style={{
                    width: '300px',
                    height: 'auto',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    marginTop: '10px',
                    display: 'block',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            )}
          </div>
        );
      }

      case 'variable': {
        const value =
          watchedValues[field.name] || resolveVariable(field.variableKey || 'card.name');
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label || field.variableLabel || 'Variable'}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input
              type="text"
              {...register(field.name, { required: field.required })}
              defaultValue={value}
              readOnly={!!field.readOnly}
              placeholder={field.placeholder || field.variableLabel || ''}
            />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );
      }

      case 'wheelbase_vehicle':
        return (
          <AsyncVehicleSelect
            key={fieldKey}
            field={field}
            accessToken={accessToken}
            register={register}
            setValue={setValue}
            errors={errors}
            styles={styles}
          />
        );

      case 'wheelbase_booking':
        return (
          <AsyncBookingSelect
            key={fieldKey}
            field={field}
            accessToken={accessToken}
            register={register}
            setValue={setValue}
            errors={errors}
            styles={styles}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="large"
      className="fullscreenInspectionModal"
      closeOnDimmerClick={false}
      closeOnEscape={false}
    >
      <Modal.Header>
        <Icon name="clipboard check" />
        {formSchema.title || form.name}
      </Modal.Header>

      <Modal.Content scrolling ref={contentRef}>
        {/* Progress bar */}
        <div className={styles.progressContainer}>
          <div className={styles.progressInfo}>
            <span>
              Section {currentSection + 1} of {totalSections}
              {isSavingDraft && (
                <Icon name="cloud upload" size="small" style={{ marginLeft: 6, opacity: 0.6 }} />
              )}
              {draftRestored && currentSection === 0 && !isSavingDraft && (
                <span style={{ marginLeft: 8, fontSize: '0.85em', color: '#2185d0' }}>
                  <Icon name="redo" size="small" /> Resumed from draft
                </span>
              )}
            </span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress percent={progress} color="blue" size="small" />
        </div>

        {showSuccessMessage && (
          <Message positive icon>
            <Icon name="info circle" />
            <Message.Content>
              <Message.Header>Analysis in Progress</Message.Header>
              <p>
                We will analyze the response. This process will take 1-2 minutes. You can close this
                window and continue with your work.
              </p>
            </Message.Content>
          </Message>
        )}

        {submitError && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{submitError}</p>
          </Message>
        )}

        {/* Current section */}
        <form
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        >
          <div className={styles.sectionHeader}>
            <h3>{currentSectionData.title}</h3>
          </div>

          <div className={styles.fieldsContainer}>
            {currentSectionData.fields
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(field => renderField(field))}
          </div>

          {/* Validation message */}
          {validationMessage && (
            <Message warning>
              <Icon name="warning sign" />
              {validationMessage}
            </Message>
          )}

          {/* Navigation buttons */}
          <div className={styles.navigation}>
            <Button type="button" onClick={handlePrevious} disabled={currentSection === 0}>
              <Icon name="chevron left" />
              Previous
            </Button>

            {currentSection === totalSections - 1 ? (
              <Button
                type="button"
                primary
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                Submit Inspection
                <Icon name="check" />
              </Button>
            ) : (
              <Button type="button" primary onClick={handleNext}>
                Next
                <Icon name="chevron right" />
              </Button>
            )}
          </div>
        </form>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
      </Modal.Actions>
    </Modal>
  );
});

FillInspectionModal.propTypes = {
  assignment: PropTypes.object.isRequired,
  cardId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default FillInspectionModal;
